import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { DateTime } from 'luxon'
import { createColumnHelper, ColumnDef } from '@tanstack/react-table'
import { SimpleTable } from 'components/tables'
import { TwoColumn } from 'components/layout'
import { workdayLogo } from 'components/integration-logos'
import { useAuth } from '@hooks/useAuth'
import { useModal } from '@hooks/useModal'
import { LinkedStatus } from './LinkedStatus'
import { WorkdayProfileDataModalContent } from './workday-profile-data'

interface WorkdaySectionProps {
  userName: string
  workdayWorkerId: number | null
  hireDate: DateTime | undefined | null
  terminationDate: DateTime | undefined | null
}

interface WorkdayRow {
  name: string
  linked: boolean
  id: number | string
  hireDate: DateTime | undefined | null
  terminationDate: DateTime | undefined | null
}

const columnHelper = createColumnHelper<WorkdayRow>()

export const WorkdaySection: FC<WorkdaySectionProps> = ({
  userName,
  workdayWorkerId,
  hireDate,
  terminationDate,
}) => {
  const { t } = useTranslation()
  const { user: signedInUser } = useAuth()
  const modal = useModal()

  const openProfileData = (employeeId: number) => {
    const id = modal.open({
      variant: 'custom',
      title: t('features.usersAdmin.userDetails.workdayProfileData.title', {
        name: userName,
      }),
      closeOnBackgroundClick: false,
      content: <WorkdayProfileDataModalContent employeeId={employeeId} />,
      onClose: () => modal.close(id),
    })
  }

  const formatDate = (date: DateTime | undefined | null) =>
    date ? date.toLocaleString(DateTime.DATE_FULL) : t('common.notAvailable')

  const data: WorkdayRow[] = useMemo(
    () => [
      {
        name: t('features.usersAdmin.userDetails.workday'),
        linked: workdayWorkerId != null,
        id: workdayWorkerId ?? t('common.notAvailable'),
        hireDate,
        terminationDate,
      },
    ],
    [workdayWorkerId, hireDate, terminationDate, t],
  )

  const columns = [
    columnHelper.accessor('name', {
      id: 'name',
      header: t('features.usersAdmin.userDetails.workday'),
      cell: ({ getValue }) => (
        <span className="flex items-center gap-3">
          <img
            src={workdayLogo}
            alt={t('features.usersAdmin.userDetails.workdayLogoAlt')}
            className="w-5 h-5"
          />
          {getValue()}
        </span>
      ),
    }),
    columnHelper.accessor((row) => row.linked, {
      id: 'status',
      header: t('common.status'),
      cell: ({ getValue }) => <LinkedStatus linked={getValue()} />,
    }),
    columnHelper.accessor('id', {
      header: t('features.usersAdmin.userDetails.workdayId'),
    }),
    columnHelper.accessor('hireDate', {
      id: 'hireDate',
      header: t('features.usersAdmin.hireDate'),
      cell: ({ getValue }) => formatDate(getValue()),
    }),
    columnHelper.accessor('terminationDate', {
      id: 'terminationDate',
      header: t('features.usersAdmin.terminationDate'),
      cell: ({ getValue }) => formatDate(getValue()),
    }),
    // Raw Workday data is only available to developers
    ...(signedInUser?.developer
      ? [
          columnHelper.display({
            id: 'actions',
            header: t(
              'features.usersAdmin.userDetails.workdayProfileData.actions',
            ),
            cell: () =>
              workdayWorkerId != null && (
                <button
                  className="text-hyperlink-blue hover:underline"
                  onClick={() => openProfileData(workdayWorkerId)}
                >
                  {t(
                    'features.usersAdmin.userDetails.workdayProfileData.viewProfileData',
                  )}
                </button>
              ),
          }),
        ]
      : []),
  ] as ColumnDef<WorkdayRow>[]

  return (
    <TwoColumn
      label={t('features.usersAdmin.userDetails.workday')}
      description={t('features.usersAdmin.userDetails.workdayDescription')}
    >
      <SimpleTable
        className="w-[825px]"
        columns={columns}
        data={data}
        defaultSortColumn="name"
      />
    </TwoColumn>
  )
}
